import React, { useMemo, useContext } from "react";
import { queryCache, QueryKey, useMutation } from "react-query";
import { ICellRendererParams } from "@ag-grid-community/core";
import { AssigneeSelector } from "fe-shared/src/components";
import UserContext from "contexts/userContext";
import {
  GridInvoice,
  InvoiceStatusCode,
} from "fe-shared-transactions/src/types";

import * as api from "../modules/api";
import { INVOICE_APPROVAL_ROLES } from "consts";

const assignStatuses = [
  InvoiceStatusCode.Submitted,
  InvoiceStatusCode.InReview,
  InvoiceStatusCode.InApproval,
];

const possibleRoles = [
  INVOICE_APPROVAL_ROLES.InvoiceApprovalCEO,
  INVOICE_APPROVAL_ROLES.InvoiceApprovalExecutive,
  INVOICE_APPROVAL_ROLES.InvoiceApprovalDirector,
  INVOICE_APPROVAL_ROLES.InvoiceApprovalManager,
  INVOICE_APPROVAL_ROLES.InvoiceApprovalClerk
];

interface Props extends ICellRendererParams {
  canAssign?: boolean;
  data: GridInvoice;
  listQueryKey: QueryKey;
  activitiesQueryKey?: QueryKey;
}

const AssigneeRenderer = ({
  canAssign = true,
  data,
  listQueryKey,
  activitiesQueryKey,
}: Props) => {
  const { invoiceId, assignee, statusCode } = data;
  const { userId: currentUserId } = useContext(UserContext)!;
  const { userId: assigneeId, displayName: assigneeDisplayName } = assignee!;

  const isAssignable = useMemo(
    () => canAssign && assignStatuses.includes(statusCode),
    [statusCode, canAssign]
  );
  const isAssigned = !!assignee?.userId;

  const [changeAssignee] = useMutation(api.assignInvoice, {
    throwOnError: true,
    onSuccess: () => {
      queryCache.invalidateQueries(listQueryKey);

      if (activitiesQueryKey) {
        queryCache.invalidateQueries(activitiesQueryKey);
      }
    },
  });

  const onChange = (userId: string | number | null) => {
    changeAssignee({
      userId,
      invoiceId,
    });
  };

  return (
    <AssigneeSelector
      entityId={invoiceId.toString()}
      isAssignable={isAssignable}
      isUnAssignable={false}
      loadOnlyActive={true}
      isAssigned={isAssigned}
      currentUserId={currentUserId}
      assigneeId={String(assigneeId)}
      assigneeName={assigneeDisplayName}
      filteringRoles={possibleRoles}
      onChange={onChange}
    />
  );
};

export default AssigneeRenderer;
